import { Menu } from 'lucide-react'
import { useLocation, matchPath } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { useAuth } from '../hooks/useAuth'
import { getMe, getMeMtdSummary } from '../lib/api'

function formatCurrency(value) {
  return new Intl.NumberFormat('en-AU', {
    style: 'currency',
    currency: 'AUD',
    minimumFractionDigits: 2,
  }).format(value ?? 0)
}

const routeTitles = {
  '/dashboard':      'Dashboard',
  '/orders':         'Orders',
  '/invoices':       'Invoices',
  '/admin':          'Admin Overview',
  '/admin/clients':  'Clients',
}

function getTitle(pathname) {
  if (pathname.startsWith('/admin/clients/')) return 'Client Detail'
  if (pathname === '/admin/profit-loss' || pathname.startsWith('/admin/profit-loss/')) return 'Profit & Loss'
  return routeTitles[pathname] || 'Portal'
}

export default function Header({ onMenuClick }) {
  const { user, role } = useAuth()
  const { pathname } = useLocation()
  const title = getTitle(pathname)

  // Resolve client_id the same way Account.jsx does — via /me/. Reading from
  // user_metadata is unreliable on the new auth model (the client_id isn't
  // stamped there for invited users; the canonical link is in client_users).
  // We share TanStack cache keys with the Overview page so this isn't a
  // separate fetch when the user is sitting on /account.
  const { data: me } = useQuery({
    queryKey: ['me'],
    queryFn:  getMe,
    enabled:  role === 'client',
    staleTime: 60_000,
  })

  // Track the URL-selected client so multi-client users see header totals
  // for the SAME client the page below is rendering. useParams from this
  // component only reflects the route Header is mounted on (it's outside
  // the /account/:clientId outlet), so use matchPath against the location
  // pathname to extract the clientId from /account/:clientId(/:tab)?.
  const accountMatch =
    matchPath('/account/:clientId/:tab', pathname) ||
    matchPath('/account/:clientId',      pathname)
  const routeClientId = accountMatch?.params?.clientId || null
  const allowedIds = (me?.clients || []).map(c => c.id)
  const clientId =
    routeClientId && allowedIds.includes(routeClientId)
      ? routeClientId
      : (me?.clients?.[0]?.id || null)

  const { data: summary, isLoading: summaryLoading } = useQuery({
    queryKey: ['me', 'mtd-summary', clientId],
    queryFn:  () => getMeMtdSummary(clientId),
    enabled:  role === 'client' && !!clientId,
    refetchInterval: 120_000,
  })
  const mtdTotal    = summary?.mtd_total    ?? 0
  const outstanding = summary?.outstanding  ?? 0
  const overdue     = !!summary?.overdue
  // Three states matter: (a) waiting on /me, (b) /me loaded + waiting on
  // summary, (c) /me loaded but no client linked. The previous chain
  // collapsed (c) to a rendered $0.00 — instead, hide the stats block
  // entirely when there's no client to summarize (see `hasClient` guard
  // around the block below).
  const hasClient    = role === 'client' && !!clientId
  const statsLoading = role === 'client' && (!me || (hasClient && summaryLoading))

  return (
    <header className="bg-white border-b border-slate-200 px-4 lg:px-6 py-3 flex items-center gap-4 flex-shrink-0">
      {/* Mobile menu button */}
      <button
        onClick={onMenuClick}
        className="lg:hidden p-2 rounded-lg text-slate-500 hover:bg-slate-100 transition-colors"
        aria-label="Open menu"
      >
        <Menu size={20} />
      </button>

      {/* Page title */}
      <h1 className="text-lg font-semibold text-slate-800 flex-shrink-0">{title}</h1>

      <div className="flex-1" />

      {/* Live stats — client only. MTD Total + Outstanding pull from
          /me/clients/{id}/mtd-summary keyed on the URL-selected client so
          multi-client users get header totals that track the page below.
          Outstanding turns red only when xero_overdue=true (past due date)
          to match admin — coloring on outstanding>0 alone falsely brands
          current-but-unpaid invoices as delinquent. */}
      {role === 'client' && (hasClient || statsLoading) && (
        <div className="hidden md:flex items-center gap-5 border-r border-slate-200 pr-5 mr-1">
          {statsLoading ? (
            <div className="flex gap-4">
              {[1, 2].map((i) => (
                <div key={i} className="skeleton h-8 w-24 rounded" />
              ))}
            </div>
          ) : (
            <>
              <div className="text-right">
                <p className="text-xs text-slate-400 leading-none mb-0.5">MTD Total</p>
                <p className="text-sm font-semibold text-slate-800">{formatCurrency(mtdTotal)}</p>
              </div>

              <div className="text-right">
                <p className="text-xs text-slate-400 leading-none mb-0.5">Outstanding</p>
                <p className={[
                  'text-sm font-semibold',
                  overdue ? 'text-red-500' : 'text-slate-800',
                ].join(' ')}>
                  {formatCurrency(outstanding)}
                </p>
              </div>
            </>
          )}
        </div>
      )}

      {/* User badge */}
      {user && (
        <div className="flex items-center gap-2">
          <div className="text-right hidden sm:block">
            <p className="text-xs font-medium text-slate-700 truncate max-w-[180px]">{user.email}</p>
          </div>
          <span className={[
            'badge text-xs px-2 py-1',
            role === 'admin'
              ? 'bg-amber-100 text-amber-700'
              : 'bg-blue-100 text-blue-700',
          ].join(' ')}>
            {role}
          </span>
        </div>
      )}
    </header>
  )
}
